import EmptyTableState from 'components/EmptyTableState';
import LoadingIndicator from 'components/LoadingIndicator';

import React, { useState } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';

import { getSelectedCustomerOutcome } from 'selectors/useCasePage';
import { useSelector } from 'store';
import { type Category } from 'types';
import CategoriesListActionRow from './CategoriesListActionRow';
import CategoryListItem from './CategoryListItem';
import EditBulkOutcomesModal from './EditBulkOutcomesModal';

type ComponentProps = {
  categories: Category[];
  loading?: boolean;
};

export type groupedCategoryByRule = {
  uuid: string;
  name: string;
  description: string;
  categories: Category[];
};

export const groupCategories = (categories: Category[]): Record<string, groupedCategoryByRule> =>
  categories.reduce<Record<string, groupedCategoryByRule>>((acc, curr) => {
    if (acc[curr.rule?.rule_group_uuid || '']) {
      acc[curr.rule?.rule_group_uuid || ''].categories.push(curr);
    } else {
      acc[curr.rule?.rule_group_uuid || ''] = {
        uuid: curr.rule?.rule_group_uuid || '',
        name: curr.rule?.name || '',
        description: curr.rule?.description || '',
        categories: [curr],
      };
    }
    return acc;
  }, {});

const CategoriesByRuleTable: React.FC<ComponentProps> = ({ categories, loading }) => {
  const selectedCustomerOutcome = useSelector(getSelectedCustomerOutcome);

  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>(selectedCustomerOutcome);
  const [isEditing, setIsEditing] = useState(false);

  const groupedCategories = groupCategories(categories);

  const toggleEditModal = (): void => setIsEditing(!isEditing);

  return (
    <LoadingOverlay
      active={loading}
      spinner={<LoadingIndicator size="10" />}
      fadeSpeed={0}
      styles={{
        content: (base) => ({
          ...base,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }),
        wrapper: () => ({}),
        spinner: () => ({}),
        overlay: (base) => ({
          ...base,
          zIndex: 10,
        }),
      }}
    >
      <div className="table-wrapper__new">
        <table className="table-wrapper__table">
          <thead>
            <tr>
              <th className="table-wrapper__new-th py-0 w-13">
                <div className="w-full pr-2 flex justify-end">
                  <input
                    id="checkbox-tag-groups"
                    data-testid="checkbox"
                    type="checkbox"
                    disabled={!!selectedCustomer}
                    className={`cursor-pointer form-checkbox litlingo-checkbox w-5 h-5 rounded-sm ${
                      selectedModels?.length > 0 &&
                      selectedModels?.length < Object.keys(groupedCategories).length
                        ? 'checkbox-select-all-partial'
                        : ''
                    }`}
                    onClick={(e): void => e.stopPropagation()}
                    onChange={(): void => {
                      if (selectedModels.length === Object.keys(groupedCategories).length) {
                        setSelectedModels([]);
                      } else {
                        setSelectedModels(Object.keys(groupedCategories).map((r) => r));
                      }
                    }}
                    checked={
                      selectedModels?.length === Object.keys(groupedCategories).length &&
                      selectedModels?.length > 0
                    }
                  />
                </div>
              </th>
              <th className="table-wrapper__new-th table-wrapper__th--w-22">Model/Customer</th>
              <th className="table-wrapper__new-th">Description</th>
              <th className="table-wrapper__new-th table-wrapper__th--w-12">Use</th>
              <th className="table-wrapper__new-th table-wrapper__th--w-12">State</th>
              <th className="table-wrapper__new-th table-wrapper__th--w-16">Staging Modified By</th>
            </tr>
            {(selectedModels?.length >= 1 || selectedCustomer) && (
              <CategoriesListActionRow
                toggleModalOpen={toggleEditModal}
                showPerformance={Object.values(groupedCategories).length === selectedModels.length}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
            )}
          </thead>
          <tbody className="table-wrapper__tbody" data-testid="categories-table">
            {!loading && categories?.length === 0 ? (
              <EmptyTableState message="There are no models to display" colCount="5" />
            ) : (
              Object.values(groupedCategories)
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                .map((ruleCategory) => (
                  <CategoryListItem
                    key={ruleCategory.uuid}
                    ruleCategory={ruleCategory}
                    selected={selectedModels.includes(ruleCategory.uuid)}
                    selectedModels={selectedModels}
                    setSelectedModels={setSelectedModels}
                    selectedCustomer={selectedCustomer}
                    setSelectedCustomer={setSelectedCustomer}
                  />
                ))
            )}
          </tbody>
        </table>
        {isEditing && (
          <EditBulkOutcomesModal
            selectedModels={selectedModels}
            isModalOpen={isEditing}
            toggleModalOpen={toggleEditModal}
            categories={categories}
          />
        )}
      </div>
    </LoadingOverlay>
  );
};

export default CategoriesByRuleTable;
